import { useEffect, useRef, useState } from 'react';
import { FormProps } from 'types/global.type';
import { Form, useFormikContext } from 'formik';
import ReCAPTCHA from 'react-google-recaptcha';
import Button from 'components/global/Button';
import FormElement from 'components/global/FormElement';
import FormErrorMessage from 'components/global/FormErrorMessage';
import { GOOGLE_RECAPTCHA_SITE_KEY } from 'utils/config';

const SignUpForm = ({ loading, error, successMsg }: FormProps) => {
  const { resetForm, setFieldValue, values } = useFormikContext<any>();
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const [recaptchaError, setRecaptchaError] = useState<string>('');

  useEffect(() => {
    if (successMsg) {
      resetForm();
      recaptchaRef.current?.reset();
      setRecaptchaError('');
    }
  }, [successMsg, resetForm]);

  const handleRecaptchaChange = (token: string | null) => {
    setFieldValue('recaptcha_token', token || '');
    if (token) {
      setRecaptchaError('');
    }
  };

  const handleRecaptchaError = () => {
    setRecaptchaError('reCAPTCHA verification failed. Please try again.');
    setFieldValue('recaptcha_token', '');
  };

  const handleRecaptchaExpired = () => {
    setRecaptchaError('reCAPTCHA expired. Please verify again.');
    setFieldValue('recaptcha_token', '');
  };

  // Check if the error is reCAPTCHA-related
  const isRecaptchaError = error?.toLowerCase().includes('recaptcha');
  const displayedRecaptchaError =
    recaptchaError ||
    (isRecaptchaError
      ? 'reCAPTCHA verification failed. Please try again.'
      : '');
  const nonRecaptchaError = isRecaptchaError ? '' : error;

  return successMsg ? (
    <FormErrorMessage error={successMsg} variant="standalone" type="success" />
  ) : (
    <Form data-testid="sign-up-form">
      <FormErrorMessage error={nonRecaptchaError} variant="auth" />

      <FormElement
        name="email"
        type="email"
        variant="auth"
        label="Email address"
      />
      <FormElement
        name="first_name"
        variant="auth"
        label="First Name (optional)"
      />
      <FormElement
        name="last_name"
        variant="auth"
        label="Last Name (optional)"
      />
      <FormElement
        name="password"
        type="password"
        variant="auth"
        label="Password"
      />
      <FormElement
        name="confirmPassword"
        type="password"
        variant="auth"
        label="Confirm Password"
      />

      <ReCAPTCHA
        ref={recaptchaRef}
        sitekey={GOOGLE_RECAPTCHA_SITE_KEY!}
        onChange={handleRecaptchaChange}
        onErrored={handleRecaptchaError}
        onExpired={handleRecaptchaExpired}
        data-action="signup"
      />

      {displayedRecaptchaError && (
        <FormErrorMessage error={displayedRecaptchaError} variant="auth" />
      )}

      <Button
        action="button"
        variant="brand"
        type="submit"
        isLoading={loading}
        isDisabled={!values.recaptcha_token}
      >
        Create Account
      </Button>
    </Form>
  );
};

export default SignUpForm;
